import posthog, { type PostHogConfig } from "posthog-js"
import { useEffect } from "react"
import { useConsent } from "../consent/useConsent"

export type PostHogInitOptions = {
  apiKey: string | undefined
  apiHost: string | undefined
  /** Extra init options, e.g. a stricter setup for one route. */
  config?: Partial<PostHogConfig>
  /**
   * Loads the session recorder. PostHog sampling and triggers still decide
   * what is kept. Off by default: posthog-js loads the ~200 KB recorder and
   * runs rrweb before it checks the triggers.
   */
  shouldRecord?: boolean
  /** Sent as `tt_app` on every event. Insights filter on it. */
  app: "terminal" | "landing"
  /** Sent as `tt_app_version` on every event */
  appVersion: string
}

/**
 * Initialises PostHog under the shared consent decision.
 *
 * Landing and Explorer both call this with the same project key. They are one
 * origin, so the `distinct_id` cookie follows an anonymous visitor from the
 * marketing pages into the product — `identify()` on signup then merges that
 * pre-signup history into the new account.
 *
 * Each app passes its own env values: `NEXT_PUBLIC_*` is inlined at build time
 * by the consuming app, not by this package.
 */
export function usePostHogInit({
  apiKey,
  apiHost,
  config,
  shouldRecord = false,
  app,
  appVersion,
}: PostHogInitOptions) {
  const { isResolved, isPerformanceAllowed } = useConsent()

  useEffect(() => {
    // Never initialize PostHog on the local dev server
    if (process.env.NODE_ENV === "development") {
      return
    }

    // Wait for the client snapshot before deciding whether to start
    if (!isResolved || !apiKey || !apiHost) {
      return
    }

    /* Rejecting performance cookies stops PostHog outright. Rejecting also
       reloads the page, so in practice this branch just never starts it on the
       fresh document. The consent beacon reports the decision over plain HTTP,
       independently of posthog-js, so decline rate stays measurable. */
    if (!isPerformanceAllowed) {
      return
    }

    if (!posthog.__loaded) {
      posthog.init(apiKey, {
        api_host: apiHost,
        defaults: "2025-11-30",
        // Started below, only where the app asks for it
        disable_session_recording: true,
        // posthog-js loads surveys.js even when no survey is active. Remove
        // this before you run a survey.
        disable_surveys: true,
        // Per event, not register(): reset() on logout clears registered
        // properties, and the embed `loaded` hook captures before init returns
        before_send: (event) =>
          event && {
            ...event,
            properties: {
              ...event.properties,
              tt_app: app,
              tt_app_version: appVersion,
            },
          },
        ...config,
      })
    }

    if (!shouldRecord) {
      return
    }

    try {
      if (localStorage.getItem("ph:debug-mode") === "true") {
        return
      }
    } catch {
      // Storage access can be blocked; recording stays on
    }

    posthog.startSessionRecording()
  }, [
    isResolved,
    isPerformanceAllowed,
    apiKey,
    apiHost,
    config,
    shouldRecord,
    app,
    appVersion,
  ])
}
